import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, ShieldCheck, UserPlus, Trash2, X, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ROLES = [
    { v: "admin", label: "Admin" },
    { v: "invoicing_staff", label: "Invoicing Staff" },
    { v: "accountant", label: "Accountant" },
];

const emptyForm = { email: "", name: "", role: "accountant", username: "", password: "" };

export default function UsersPage() {
    const { user: me } = useAuth();
    const [rows, setRows] = useState([]);
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    const load = () => api.get("/auth/users").then((r) => setRows(r.data));
    useEffect(() => { load(); }, []);

    const setRole = async (u, role) => {
        try {
            await api.put(`/auth/users/${u.user_id}/role`, { role });
            toast.success(`${u.email} → ${role}`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const submitAdd = async (e) => {
        e.preventDefault();
        if (!form.email) return toast.error("Email is required");
        if (form.password && form.password.length < 6) return toast.error("Password must be at least 6 characters");
        setSaving(true);
        try {
            const body = { email: form.email, name: form.name, role: form.role };
            if (form.username) body.username = form.username.trim().toLowerCase();
            if (form.password) body.password = form.password;
            await api.post("/auth/users", body);
            if (form.password) {
                toast.success(`Created ${form.email} — they can log in immediately with ${form.username ? `username "${form.username}"` : "their email"} and the password you set.`);
            } else {
                toast.success(`Invited ${form.email} as ${form.role.replace("_", " ")}. They'll get their role on first Google sign-in.`);
            }
            setShowAdd(false);
            setForm(emptyForm);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        setSaving(false);
    };

    const resetPassword = async (u) => {
        const pwd = window.prompt(`Set a new password for ${u.email}. Users can log in with either email or username + this password.`);
        if (!pwd) return;
        if (pwd.length < 6) return toast.error("Password must be at least 6 characters");
        try {
            await api.put(`/auth/users/${u.user_id}/password`, { password: pwd });
            toast.success(`Password updated for ${u.email}. Existing sessions revoked.`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const removeUser = async (u) => {
        if (!window.confirm(`Remove ${u.email}? Their sessions will be revoked immediately.`)) return;
        try {
            await api.delete(`/auth/users/${u.user_id}`);
            toast.success(`${u.email} removed`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    return (
        <div data-testid="users-page">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl hdb-heading font-black text-slate-900">Users & Roles</h1>
                    <p className="text-slate-500 mt-1 text-sm">
                        Invite teammates by pre-provisioning their email & role. New Google sign-ins that don't
                        match an invited user start as <b>Accountant</b> (read-only).
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 hdb-card px-4 py-2">
                        <Users size={14} className="text-slate-500" />
                        <span className="hdb-num text-sm font-bold text-slate-900">{rows.length}</span>
                        <span className="text-xs text-slate-500 uppercase tracking-wider">Users</span>
                    </div>
                    <button className="hdb-btn hdb-btn-primary" onClick={() => setShowAdd(true)} data-testid="add-user-btn">
                        <UserPlus size={14} /> Add User
                    </button>
                </div>
            </div>

            <div className="hdb-card overflow-hidden">
                <table className="hdb-table w-full">
                    <thead>
                        <tr>
                            <th>User</th>
                            <th>Email</th>
                            <th>Auth</th>
                            <th>Current role</th>
                            <th>Change role</th>
                            <th className="w-20"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((u) => {
                            const isMe = me?.user_id === u.user_id;
                            const pending = u.auth_provider === "pending_google";
                            return (
                                <tr key={u.user_id} data-testid={`user-row-${u.email}`}>
                                    <td className="font-medium text-slate-900">
                                        {u.name || "—"}
                                        {isMe && <span className="hdb-tag hdb-tag-mint ml-1">You</span>}
                                        {pending && <span className="hdb-tag hdb-tag-gold ml-1">Invited</span>}
                                    </td>
                                    <td className="hdb-mono text-xs">{u.email}</td>
                                    <td>
                                        <span className="hdb-tag hdb-tag-slate">{u.auth_provider || "-"}</span>
                                    </td>
                                    <td>
                                        <span className={`hdb-tag ${u.role === "admin" ? "hdb-tag-gold" : u.role === "invoicing_staff" ? "hdb-tag-mint" : "hdb-tag-slate"}`}>
                                            <ShieldCheck size={10} className="inline mr-1 -mt-0.5" />
                                            {u.role?.replace("_", " ")}
                                        </span>
                                    </td>
                                    <td>
                                        <select className="hdb-input max-w-[200px]" value={u.role}
                                                onChange={(e) => setRole(u, e.target.value)}
                                                data-testid={`role-select-${u.email}`}>
                                            {ROLES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
                                        </select>
                                    </td>
                                    <td>
                                        {!isMe && (
                                            <div className="flex gap-1 justify-end">
                                                <button className="hdb-btn hdb-btn-ghost"
                                                        title="Set or reset password"
                                                        onClick={() => resetPassword(u)}
                                                        data-testid={`reset-pwd-${u.email}`}>
                                                    <KeyRound size={12} />
                                                </button>
                                                <button className="hdb-btn hdb-btn-danger" onClick={() => removeUser(u)}
                                                        data-testid={`delete-user-${u.email}`}>
                                                    <Trash2 size={12} />
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showAdd && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm" data-testid="add-user-modal">
                    <div className="hdb-card w-full max-w-md p-6 shadow-2xl">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="hdb-heading font-bold text-lg text-slate-900">Invite a new user</h3>
                            <button className="text-slate-400 hover:text-slate-600" onClick={() => setShowAdd(false)}>
                                <X size={18} />
                            </button>
                        </div>
                        <p className="text-xs text-slate-500 mb-4">
                            Two ways to onboard a user:
                            <span className="block mt-1">• <b>Direct login</b> — set a username + password below; user can log in immediately.</span>
                            <span className="block">• <b>Google sign-in</b> — leave password blank; they'll get the role when they Google-sign-in with this email.</span>
                        </p>
                        <form onSubmit={submitAdd} className="space-y-3">
                            <div>
                                <label className="hdb-label">Email *</label>
                                <input className="hdb-input" type="email" value={form.email}
                                       onChange={(e) => setForm({ ...form, email: e.target.value })}
                                       placeholder="teammate@company.com"
                                       required data-testid="new-user-email" />
                            </div>
                            <div>
                                <label className="hdb-label">Name (optional)</label>
                                <input className="hdb-input" value={form.name}
                                       onChange={(e) => setForm({ ...form, name: e.target.value })}
                                       placeholder="Displayed on the users list"
                                       data-testid="new-user-name" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="hdb-label">Username (optional)</label>
                                    <input className="hdb-input hdb-mono" value={form.username}
                                           onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g,"") })}
                                           placeholder="e.g. rakesh"
                                           data-testid="new-user-username" />
                                    <div className="text-[10px] text-slate-400 mt-1">Short login handle (a-z, 0-9, . _ -)</div>
                                </div>
                                <div>
                                    <label className="hdb-label">Password (optional)</label>
                                    <input className="hdb-input hdb-mono" type="text" value={form.password}
                                           onChange={(e) => setForm({ ...form, password: e.target.value })}
                                           placeholder="min 6 chars"
                                           data-testid="new-user-password" />
                                    <div className="text-[10px] text-slate-400 mt-1">Leave blank for Google sign-in only</div>
                                </div>
                            </div>
                            <div>
                                <label className="hdb-label">Role</label>
                                <select className="hdb-input" value={form.role}
                                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                                        data-testid="new-user-role">
                                    {ROLES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
                                </select>
                                <div className="text-[10px] text-slate-500 mt-1">
                                    <b>Admin</b>: full access, manage users. <b>Invoicing Staff</b>: create/edit invoices. <b>Accountant</b>: read-only + reports.
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <button type="button" className="hdb-btn hdb-btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
                                <button type="submit" className="hdb-btn hdb-btn-primary" disabled={saving} data-testid="save-new-user">
                                    {saving ? "Saving…" : (form.password ? "Create with login" : "Send invite")}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
